import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faStop,
  faCopy,
  faVolumeUp,
  faCode,
  faCheck,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { height, width } from "@fortawesome/free-brands-svg-icons/fa11ty";

// Morse code mapping (from your Python code)
const charToMorse = {
  "!": "-.-.--",
  '"': ".-..-.",
  $: "...-..-",
  "&": ".-...",
  "'": ".----.",
  "(": "-.--.",
  ")": "-.--.-",
  "+": ".-.-.",
  ",": "--..--",
  "-": "-....-",
  ".": ".-.-.-",
  "/": "-..-.",
  0: "-----",
  1: ".----",
  2: "..---",
  3: "...--",
  4: "....-",
  5: ".....",
  6: "-....",
  7: "--...",
  8: "---..",
  9: "----.",
  ":": "---...",
  ";": "-.-.-.",
  "=": "-...-",
  "?": "..--..",
  "@": ".--.-.",
  _: "..--.-",
  A: ".-",
  B: "-...",
  C: "-.-.",
  D: "-..",
  E: ".",
  F: "..-.",
  G: "--.",
  H: "....",
  I: "..",
  J: ".---",
  K: "-.-",
  L: ".-..",
  M: "--",
  N: "-.",
  O: "---",
  P: ".--.",
  Q: "--.-",
  R: ".-.",
  S: "...",
  T: "-",
  U: "..-",
  V: "...-",
  W: ".--",
  X: "-..-",
  Y: "-.--",
  Z: "--..",
  " ": " ",
};

const MorseConverter = () => {
  const [inputText, setInputText] = useState("");
  const [morseCode, setMorseCode] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const playbackRef = useRef(null);

  // Convert text to Morse code
  const convertToMorse = () => {
    const text = inputText.toUpperCase();
    let morse = "";

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char in charToMorse) {
        if (char === " ") {
          morse += "  "; // Word separator
        } else {
          morse += charToMorse[char] + " "; // Character separator
        }
      }
    }

    setMorseCode(morse.trim());
  };

  // Generate WAV file using the same logic as Python code
  const generateWav = (freq, duration, framerate = 4410, amplitude = 0.5) => {
    // Create samples
    const samples = Math.floor(framerate * duration);
    const data = new Float32Array(samples);

    for (let i = 0; i < samples; i++) {
      data[i] = amplitude * Math.sin((2 * Math.PI * freq * i) / framerate);
    }

    // Convert to 16-bit PCM
    const pcm = new Int16Array(samples);
    for (let i = 0; i < samples; i++) {
      pcm[i] = Math.max(-32767, Math.min(32767, Math.floor(data[i] * 32767)));
    }

    // Create WAV file
    const buffer = new ArrayBuffer(44 + pcm.length * 2);
    const view = new DataView(buffer);

    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + pcm.length * 2, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, framerate, true);
    view.setUint32(28, framerate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, "data");
    view.setUint32(40, pcm.length * 2, true);

    // Write PCM data
    let offset = 44;
    for (let i = 0; i < pcm.length; i++) {
      view.setInt16(offset, pcm[i], true);
      offset += 2;
    }

    return buffer;
  };

  // Convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // Play Morse code sound
  const playMorseCode = async () => {
    if (!morseCode || isPlaying) return;

    setIsPlaying(true);
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Generate sounds
    const ditSound = generateWav(800, 0.1, 4410, volume);
    const dahSound = generateWav(800, 0.3, 4410, volume);

    const ditBase64 = arrayBufferToBase64(ditSound);
    const dahBase64 = arrayBufferToBase64(dahSound);

    const ditAudio = new Audio(`data:audio/wav;base64,${ditBase64}`);
    const dahAudio = new Audio(`data:audio/wav;base64,${dahBase64}`);

    const sequence = morseCode.split("");
    let delay = 100;

    for (let i = 0; i < sequence.length; i++) {
      const char = sequence[i];

      if (char === ".") {
        setTimeout(() => ditAudio.cloneNode().play(), delay);
        delay += 100; // dit duration
      } else if (char === "-") {
        setTimeout(() => dahAudio.cloneNode().play(), delay);
        delay += 300; // dah duration
      } else if (char === " ") {
        // Character space
        delay += 100;
      } else {
        // Word space or other characters
        delay += 300;
      }

      // Add small gap between elements
      delay += 50;
    }

    setTimeout(() => setIsPlaying(false), delay);
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    // Stop all audio by reloading the page audio context
    if (playbackRef.current) {
      playbackRef.current = null;
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(morseCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const containerStyle = {
    minHeight: "100%",
    backgroundColor: "#050069ff",
    color: "white",
    fontFamily: '"Courier New", monospace',
    paddingTop: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const headerStyle = {
    textAlign: "center",
    marginBottom: "30px",
    paddingBottom: "20px",
    width: "100%",
  };

  const cardStyle = {
    backgroundColor: "rgba(0, 255, 136, 0.05)",
    width: "80%",
    //border: "2px solid #00ff88",
    borderRadius: "10px",
    padding: "25px",
    marginBottom: "20px",
    boxShadow: "0 5px 20px rgba(0, 0, 0, 0.23)",
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "space-evenly",
  };

  const inputStyle = {
    width: "90%",
    padding: "15px 0px 15px 15px",
    backgroundColor: "rgba(0, 255, 136, 0.1)",
    border: "2px solid #5a88ffff",
    borderRadius: "6px",
    color: "#a5d1ffff",
    fontFamily: '"Courier New", monospace',
    fontSize: "16px",
    resize: "vertical",
    outline: "none",
  };

  const outputStyle = {
    width: "90%",
    padding: "15px 0px 15px 15px",
    backgroundColor: "rgba(0, 255, 136, 0.05)",
    border: "2px solid #5a88ffff",
    borderRadius: "6px",
    color: "#a5d1ffff",
    fontFamily: '"Courier New", monospace',
    fontSize: "18px",
    fontWeight: "bold",
    wordBreak: "break-word",
  };

  const buttonStyle = {
    padding: "12px 24px",
    backgroundColor: "#85a4ffff",
    color: "#050069ff",
    border: "none",
    borderRadius: "6px",
    fontFamily: '"Courier New", monospace',
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const buttonGroupStyle = {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginTop: "15px",
  };

  const volumeStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "15px",
  };

  const sliderStyle = {
    width: "150px",
    height: "6px",
    appearance: "none",
    background: "linear-gradient(90deg, #537effff, #85a4ffff)",
    borderRadius: "5px",
    outline: "none",
    opacity: "0.9",
    transition: "background 0.3s ease, opacity 0.2s",
    cursor: "pointer",
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <FontAwesomeIcon
          icon={faCode}
          size="3x"
          style={{ marginBottom: "10px" }}
        />
        <h1 style={{ margin: "10px 0", fontSize: "2.5em" }}>
          MORSE CODE CONVERTER
        </h1>
        <p style={{ margin: 0, color: "#ccc", fontSize: "1.1em" }}>
          Convert text to Morse code with audio playback
        </p>
      </div>

      {/* Input Card */}
      <div style={cardStyle}>
        <h2
          style={{
            marginTop: 0,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FontAwesomeIcon icon={faArrowRight} />
          Input Text
        </h2>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Enter text to convert to Morse code..."
          style={inputStyle}
        />
        <div style={buttonGroupStyle}>
          <button
            onClick={convertToMorse}
            style={buttonStyle}
            onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
          >
            <FontAwesomeIcon icon={faCode} />
            Convert to Morse
          </button>
        </div>
      </div>

      {/* Output Card */}
      {morseCode && (
        <div style={cardStyle}>
          <h2
            style={{
              marginTop: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <FontAwesomeIcon icon={faVolumeUp} />
            Morse Code Output
          </h2>
          <div style={outputStyle}>{morseCode}</div>

          <div style={buttonGroupStyle}>
            <button
              onClick={isPlaying ? stopPlayback : playMorseCode}
              style={{
                ...buttonStyle,
                backgroundColor: isPlaying ? "#ff4444" : "#85a4ffff",
              }}
              onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
              onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
            >
              <FontAwesomeIcon icon={isPlaying ? faStop : faPlay} />
              {isPlaying ? "Stop Playback" : "Play Sound"}
            </button>

            <button
              onClick={copyToClipboard}
              style={{
                ...buttonStyle,
                backgroundColor: copied ? "#5e87ffff" : "#85a4ffff",
              }}
              onMouseEnter={(e) => {
                if (!copied) e.target.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                if (!copied) e.target.style.transform = "scale(1)";
              }}
            >
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              {copied ? "Copied!" : "Copy Morse Code"}
            </button>
          </div>

          {/* Volume Control */}
          <div style={volumeStyle}>
            <span
              style={{
                fontFamily: '"Courier New", monospace',
                fontWeight: "bold",
              }}
            >
              Volume:
            </span>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              style={sliderStyle}
            />
            <span
              style={{
                fontFamily: '"Courier New", monospace',
                fontWeight: "bold",
              }}
            >
              {Math.round(volume * 100)}%
            </span>
          </div>
        </div>
      )}

      <style>
        {`
          button:hover:not(:disabled) {
            transform: scale(1.05);
          }
          
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #4766c3ff;
            cursor: pointer;
          }
          
          input[type="range"]::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #4766c3ff;
            cursor: pointer;
            border: none;
          }
        `}
      </style>
    </div>
  );
};

export default MorseConverter;
